import {
  type FieldValues,
  type FormItemWrapperProps,
  type Path,
  type PathValue,
  type UseControllerProps,
  useCustomController,
} from '@faceup/form'
import { UntitledIcon } from '@faceup/icons'
import { ulCheckCircle } from '@faceup/icons/ulCheckCircle'
import { FormItem } from '@faceup/ui'
import { Flex, Input, Typography, useUiBaseConfig } from '@faceup/ui-base'
import { useState } from 'react'
import { defineMessages, useIntl } from '../../TypedIntl'

const messages = defineMessages({
  otherOption: 'FollowUp.SurveySingleChoice.otherOption',
})

type Option = {
  label: string
  value: string
}

type UiSelectProps<T> = {
  options: T[]
}

type SurveySingleChoiceProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<UiSelectProps<Option> & FormItemWrapperProps, 'value' | 'defaultValue'> & {
    hasOtherOption: boolean
    name: string
    inputKey?: string
  }

type OptionComponentProps = {
  option: Option
  isActive: boolean
  onClick: (event: React.MouseEvent) => void
}

export const OptionComponent: React.FC<OptionComponentProps> = ({ option, isActive, onClick }) => {
  const { token } = useUiBaseConfig()

  const activeColor: React.CSSProperties = { color: '#FFFFFF' }
  const activeStyles: React.CSSProperties = {
    backgroundColor: token.colorPrimary,
    border: `1px solid ${token.colorPrimary}`,
    ...activeColor,
  }

  return (
    <Flex
      key={option.value}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        minHeight: '54px',
        borderRadius: token.borderRadiusLG,
        backgroundColor: '#FFFFFF',
        border: `1px solid ${token.colorBorder}`,
        padding: '16px',
        ...(isActive ? activeStyles : {}),
      }}
      align='center'
      justify='space-between'
    >
      <Typography.Text
        style={{
          ...(isActive ? activeColor : {}),
        }}
        className='sensitive'
      >
        {option.label}
      </Typography.Text>
      {isActive && <UntitledIcon icon={ulCheckCircle} size={20} className='min-w-5 ml-2' />}
    </Flex>
  )
}

export const SurveySingleChoice = <T extends FieldValues>(props: SurveySingleChoiceProps<T>) => {
  const {
    formItemProps,
    inputProps: { value, onChange },
  } = useCustomController({
    ...props,
    name: `${props.name}.value` as Path<T>,
    defaultValue: undefined as PathValue<T, Path<T>>,
  })

  const {
    inputProps: { value: otherValue, onChange: onOtherChange },
  } = useCustomController({
    ...props,
    name: `${props.name}.otherValue` as Path<T>,
    defaultValue: undefined as PathValue<T, Path<T>>,
  })

  const onSelectOptionChanged = (event: React.MouseEvent, optionValue: string) => {
    event.preventDefault()
    onOtherChange('')
    onChange(value === optionValue ? undefined : optionValue)
  }

  const onOtherOptionChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    onChange(undefined)
    onOtherChange(event.target.value)
  }

  const { options, hasOtherOption, inputKey } = props

  const { token } = useUiBaseConfig()

  const activeColor: React.CSSProperties = { color: '#FFFFFF' }
  const activeStyles: React.CSSProperties = {
    backgroundColor: token.colorPrimary,
    border: `1px solid ${token.colorPrimary}`,
    ...activeColor,
  }

  const { formatMessage } = useIntl()

  const [isOtherOptionActive, setIsOtherOptionActive] = useState(false)

  return (
    <FormItem {...formItemProps}>
      <Flex vertical gap='small' className='mt-3'>
        {options.map(option => {
          const isActive = value === option.value
          return (
            <OptionComponent
              key={option.value}
              option={option}
              isActive={isActive}
              onClick={event => onSelectOptionChanged(event, option.value)}
            />
          )
        })}
        {hasOtherOption && (
          <div
            style={{
              cursor: 'pointer',
              minHeight: '46px',
              borderRadius: token.borderRadiusLG,
              backgroundColor: '#FFFFFF',
              border: `1px solid ${token.colorBorder}`,
              ...(isOtherOptionActive && otherValue ? activeStyles : {}),
            }}
          >
            <div className='relative w-full'>
              <Input
                key={inputKey ?? 'other-option-input'}
                onChange={onOtherOptionChanged}
                value={otherValue ?? ''}
                aria-label={formatMessage(messages.otherOption)}
                autoComplete='off'
                placeholder={formatMessage(messages.otherOption)}
                data-test='survey-single-choice'
                variant='borderless'
                className='w-full pr-10 py-4'
                style={{
                  padding: '16px',
                  color: isOtherOptionActive && otherValue ? '#fff' : '#000',
                }}
                onFocus={() => {
                  setIsOtherOptionActive(false)
                }}
                onBlur={() => {
                  setIsOtherOptionActive(!!otherValue)
                }}
                maxLength={250}
              />
              {isOtherOptionActive && otherValue && (
                <UntitledIcon
                  icon={ulCheckCircle}
                  size={20}
                  className='absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none'
                />
              )}
            </div>
          </div>
        )}
      </Flex>
    </FormItem>
  )
}
